import { store } from '../store/store';
import { selectScreenState, selectSubmissionState, selectUserPermissions } from '../store/selectors';
import { Submission } from '../store/submission/types';
import { User } from '../store/user/types';
import { Role } from '../store/conference/types';
import { Review } from '../store/review/types';
import { Form } from '../store/form/types';
import { PermissionString } from '../store/permission/types';
import { PaperStatusName } from '../store/paper-status/types';
import { CategoryEnum } from '../store/paper-status/types.d';
import { getPaperStatusCategory, getPaperStatusName } from './submission';

const hasPermission = (permission: PermissionString, permissions: PermissionString[]) => {
  return permissions.includes(permission);
};

export const can = (permissionStr: PermissionString): boolean => {
  const userPermissions = selectUserPermissions(store.getState()) ?? [];
  return hasPermission(permissionStr, userPermissions);
};

export class SubmissionPermissionManager {
  submission: Submission;

  constructor(submission: Submission) {
    this.submission = submission;
  }

  isAuthor(personId: number, role: Role): boolean {
    return !!this.submission.authors.find((author) => author.id == personId) && role.type == 'author';
  }

  isPrimaryAuthor(personId: number, role: Role): boolean {
    return (
      !!this.submission.authors.find((author) => author.id == personId && author.is_primary) && role.type == 'author'
    );
  }

  isPendingAuthor(personId: number, role: Role): boolean {
    return (
      !!this.submission.authors.find((author) => author.id == personId && author.pending_confirmation) &&
      role.type == 'author'
    );
  }

  canEdit(user: User, role: Role, forms: Form[]): boolean {
    const isRejected = getPaperStatusCategory(this.submission) === CategoryEnum.Rejection;
    const paperStatusName = getPaperStatusName(this.submission);
    const isChair = role.type === 'chair';
    const numWritable = forms.filter((form) => {
      if (form.visibility !== 'write') return false;
      // Review Rating stays editable when paper status is Rejected - Phase 1
      if (isRejected) {
        return form.name === 'Review Rating' && paperStatusName === 'Rejected - Phase 1';
      }
      return true;
    }).length;

    return isChair || (this.isPrimaryAuthor(user.person.id, role) && numWritable > 0);
  }

  canViewDiscussion(user: User, role: Role): boolean {
    return (
      can('ACCESS_DISCUSSION') &&
      (role.type != 'author' || this.isPrimaryAuthor(user.person.id, role)) && // If author needs to be primary
      (this.submission.discussion.has_discussion || this.canEditDiscussion())
    );
  }

  canEditDiscussion(): boolean {
    const state = store.getState();
    const screen = selectScreenState(state).screenByName['Discussion'];
    return (
      can('ACCESS_DISCUSSION') &&
      screen.settings.enabled &&
      (this.submission.discussion.allowed_by_status ??
        screen.settings.paper_statuses.includes(this.submission.paper_status_id))
    );
  }

  canEditPaperStatus(role: Role): boolean {
    let canEdit: boolean;

    // Check role
    if (role.type == 'author') {
      canEdit = false;
    } else {
      // Check submission is not DELETED
      const name = getPaperStatusName(this.submission);
      canEdit = !(['Deleted'] as PaperStatusName[]).includes(name);
      canEdit = canEdit && can('CAN_EDIT_PAPER_STATUS');
    }

    return canEdit;
  }

  canViewPaperStatus(user: User, role: Role, forms: Form[]): boolean {
    return (
      role.type != 'author' || !(this.canEdit(user, role, forms) && this.submission.validation_status !== 'Complete')
    );
  }
}

export class AssignmentPermissionManager {
  assignment: Review;

  constructor(assignment: Review) {
    this.assignment = assignment;
  }

  isAuthor(personId: number, roleId: number): boolean {
    return this.assignment.person.id === personId && this.assignment.role_id === roleId;
  }

  canEdit(personId: number, role: Role, forms: Form[]): boolean {
    const isChair = role.type === 'chair';

    const state = store.getState();
    const submission = selectSubmissionState(state).submissionsById[this.assignment.submission_id];
    const isRejected = submission ? getPaperStatusCategory(submission) === CategoryEnum.Rejection : false;
    const paperStatusName = submission ? getPaperStatusName(submission) : '';

    const numWritable = forms.filter((form) => {
      if (form.visibility !== 'write') return false;
      // Review Rating stays editable when paper status is Rejected - Phase 1
      if (isRejected) {
        return form.name === 'Review Rating' && paperStatusName === 'Rejected - Phase 1';
      }
      return true;
    }).length;

    return isChair || numWritable > 0;
  }
}
